// Frame graph.
//
//   background -> cards          (4x MSAA, rgba16float, reverse-Z)
//        |
//     resolve
//        |
//   prefilter -> down x5 -> up x5   (bloom mip chain, half res down)
//        |
//   tonemap -> swapchain
//
// Everything before the tone mapper is scene-referred linear light with no upper
// clamp, which is what lets a foil highlight read as genuinely bright: it is
// allowed to reach 30x white in the buffer and then be brought down by the tone
// curve, rather than being clipped at 1.0 and turning into a flat white patch.

import { solidTexture } from './texture.ts';
import commonWgsl from './shaders/common.wgsl?raw';
import backgroundWgsl from './shaders/background.wgsl?raw';
import tableWgsl from './shaders/table.wgsl?raw';
import cardWgsl from './shaders/card.wgsl?raw';
import bloomWgsl from './shaders/bloom.wgsl?raw';
import tonemapWgsl from './shaders/tonemap.wgsl?raw';

import { GpuContext } from './device.ts';
import { CARD_VERTEX_LAYOUT, createCardMesh, destroyCardMesh, type CardMesh } from './mesh.ts';
import { normalMatrix, type Mat4, type Vec3 } from './math.ts';

const HDR_FORMAT: GPUTextureFormat = 'rgba16float';
const DEPTH_FORMAT: GPUTextureFormat = 'depth24plus';
const SAMPLE_COUNT = 4;
const BLOOM_LEVELS = 6;

/** Bytes per card in the shared dynamic uniform buffer, padded to the GPU's alignment. */
/**
 * Floats in TableParams. Counted against the WGSL struct rather than guessed:
 * a trailing vec3f aligns to 16 and silently grows the struct, which is what
 * broke BloomParams.
 *   half(2) playerBaseZ(1) opponentBaseZ(1)
 *   battlefieldZ(1) spacing(1) count(1) time(1)
 *   playerHeld(1) opponentHeld(1) contested(1) pad(1)
 *   accent(3) pad1(1)
 */
const TABLE_UNIFORM_FLOATS = 16;

// mat4(16) + normalMat(12) + params0(4) + params1(4) + params2(4) + tint(4)
const CARD_UNIFORM_FLOATS = 44;

export interface CardVisual {
  /** Stable key used to cache the bind group. */
  key: string;
  albedo: GPUTexture;
  mask: GPUTexture;
  back: GPUTexture;
}

export interface CardDraw {
  visual: CardVisual;
  model: Mat4;
  /** 0 none, 1 metal trim only, 2 rare holo, 3 epic full-face. */
  foilTier: number;
  hover: number;
  bend: number;
  bendAxis: readonly [number, number];
  highlight: number;
  reveal: number;
  seed: number;
  /** 1 for a card resting on the table, which holds still. */
  settled: number;
  /**
   * 1 for a card held in hand.
   *
   * Separate from `settled`, which a hovered table card also lowers. A hand is
   * tilted to face the camera, so every card in it sits near the specular peak
   * at once -- the same foil that reads as a highlight on the table becomes a
   * row of lamps. This damps it, and only there.
   */
  inHand: number;
  /**
   * How much of the *direct* light a held card takes, and how much ambient.
   *
   * A hand is tilted to face the camera, so its cards sit near the specular
   * peak and square-on to the key at the same time -- measured, the hand read
   * 1.6x brighter than the same art lying on the table, which is why it washed
   * out. Damping is per-card rather than a change to the light, because the
   * light is correct: it is the geometry of holding a fan up to your face that
   * is unusual, and the board must not dim with it.
   */
  handDirect: number;
  handAmbient: number;
  tint: readonly [number, number, number, number];
}

export interface Camera {
  viewProj: Mat4;
  position: Vec3;
}

export interface Environment {
  lightDir: Vec3;
  lightColor: Vec3;
  lightIntensity: number;
  skyColor: Vec3;
  groundColor: Vec3;
  ambientMix: number;
  exposure: number;

  bgTop: Vec3;
  bgBottom: Vec3;
  bgAccent: Vec3;
  bgAccentStrength: number;
  bgIntensity: number;
  bgLightPos: readonly [number, number];
  bgDust: number;
}

export interface Grade {
  bloomThreshold: number;
  bloomKnee: number;
  bloomIntensity: number;
  vignette: number;
  grain: number;
  chromatic: number;
  saturation: number;
  contrast: number;
}

export const DEFAULT_ENVIRONMENT: Environment = {
  lightDir: [0.35, 0.55, 0.76],
  lightColor: [1.0, 0.96, 0.90],
  lightIntensity: 1.5,
  skyColor: [0.10, 0.13, 0.21],
  groundColor: [0.035, 0.030, 0.045],
  ambientMix: 0.22,
  exposure: 1.0,

  // AgX is scene-referred: 0.18 is middle grey and it lifts everything below
  // that hard. A backdrop that should read as near-black therefore has to sit
  // around 0.004, not the 0.02 that looks right in a display-referred pipeline.
  bgTop: [0.0045, 0.0055, 0.0105],
  bgBottom: [0.0009, 0.0011, 0.0022],
  bgAccent: [0.30, 0.42, 0.85],
  bgAccentStrength: 0.022,
  bgIntensity: 1.0,
  bgLightPos: [0.5, 0.30],
  bgDust: 0.05,
};

export const DEFAULT_GRADE: Grade = {
  // The threshold sits above diffuse white on purpose: only the foil specular
  // and the card edge should bloom, never the artwork itself.
  bloomThreshold: 1.6,
  bloomKnee: 0.7,
  bloomIntensity: 0.34,
  vignette: 0.30,
  grain: 0.006,
  chromatic: 0.0016,
  saturation: 1.14,
  contrast: 1.06,
};

export interface Scene {
  camera: Camera;
  cards: readonly CardDraw[];
  environment: Environment;
  grade: Grade;
  time: number;
  /** The playmat under the cards. Omitted, nothing is drawn. */
  table?: TableLayout;
}

/**
 * Where the mat's zones are, in the same world units the card slots use.
 *
 * Passed in rather than hardcoded in the shader so the mat can never drift
 * from the layout it is marking: both come from `layout.ts`.
 */
export interface TableLayout {
  halfWidth: number;
  halfDepth: number;
  playerBaseZ: number;
  opponentBaseZ: number;
  battlefieldZ: number;
  battlefieldSpacing: number;
  battlefieldCount: number;
  /** Bitmask of the battlefields each side controls. */
  playerHeld: number;
  opponentHeld: number;
  accent: readonly [number, number, number];
}

interface BloomLevel {
  texture: GPUTexture;
  view: GPUTextureView;
  width: number;
  height: number;
  params: GPUBuffer;
  downBind?: GPUBindGroup;
  upBind?: GPUBindGroup;
}

export class Renderer {
  private readonly gpu: GpuContext;
  private readonly device: GPUDevice;
  private readonly mesh: CardMesh;

  private readonly frameBuffer: GPUBuffer;
  private readonly frameData = new Float32Array(36); // 144 bytes
  private readonly bgBuffer: GPUBuffer;
  private readonly bgData = new Float32Array(24); // 96 bytes
  private readonly tonemapBuffer: GPUBuffer;
  private readonly tonemapData = new Float32Array(12); // 48 bytes

  private cardBuffer: GPUBuffer;
  private cardData: Float32Array<ArrayBuffer>;
  private cardCapacity = 0;
  private readonly cardStride: number;

  private readonly linearSampler: GPUSampler;
  private readonly clampSampler: GPUSampler;

  private readonly bgPipeline: GPURenderPipeline;
  private readonly cardPipeline: GPURenderPipeline;
  private readonly prefilterPipeline: GPURenderPipeline;
  private readonly downPipeline: GPURenderPipeline;
  private readonly upPipeline: GPURenderPipeline;
  private readonly tonemapPipeline: GPURenderPipeline;

  private readonly bgBind: GPUBindGroup;
  /** The playmat: one ground quad marking the zones the cards sit in. */
  private readonly tablePipeline: GPURenderPipeline;
  private readonly tableBuffer: GPUBuffer;
  private tableBind: GPUBindGroup;
  private tableParamLayout!: GPUBindGroupLayout;
  /** The printed mat. Replaced by `setPlaymat`; see `playmat.ts`. */
  private matTexture: GPUTexture;
  /** Whether this renderer owns the mat texture and must destroy it. */
  private matOwned: boolean;
  private matSampler!: GPUSampler;
  private readonly tableData = new Float32Array(TABLE_UNIFORM_FLOATS);
  private readonly frameBind: GPUBindGroup;
  private readonly cardBinds = new Map<string, GPUBindGroup>();

  // Size-dependent resources, rebuilt on resize.
  private msaa!: GPUTexture;
  private msaaView!: GPUTextureView;
  private resolve!: GPUTexture;
  private resolveView!: GPUTextureView;
  private depth!: GPUTexture;
  private depthView!: GPUTextureView;
  private bloom: BloomLevel[] = [];
  private prefilterBind!: GPUBindGroup;
  private tonemapBind!: GPUBindGroup;
  private sized = false;

  private disposeResize: () => void;

  constructor(gpu: GpuContext) {
    this.gpu = gpu;
    const device = (this.device = gpu.device);

    this.mesh = createCardMesh(device);

    const align = device.limits.minUniformBufferOffsetAlignment || 256;
    this.cardStride = Math.ceil((CARD_UNIFORM_FLOATS * 4) / align) * align;

    this.frameBuffer = device.createBuffer({
      label: 'frame-uniform',
      size: this.frameData.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.bgBuffer = device.createBuffer({
      label: 'background-uniform',
      size: this.bgData.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.tonemapBuffer = device.createBuffer({
      label: 'tonemap-uniform',
      size: this.tonemapData.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.cardBuffer = device.createBuffer({
      label: 'card-uniform',
      size: this.cardStride,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.cardData = new Float32Array(this.cardStride / 4);
    this.cardCapacity = 1;

    // Anisotropy matters here: a card seen at a shallow angle is exactly the
    // case trilinear filtering blurs into mush, and the card text has to stay
    // readable at the far edge of the board.
    this.linearSampler = device.createSampler({
      label: 'sampler:aniso',
      magFilter: 'linear', minFilter: 'linear', mipmapFilter: 'linear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
      maxAnisotropy: 16,
    });
    this.clampSampler = device.createSampler({
      label: 'sampler:post',
      magFilter: 'linear', minFilter: 'linear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
    });
    // The mat is seen at a shallow angle across most of its area, so it wants
    // the same anisotropy the cards do -- without it the far half's zone rules
    // and labels dissolve.
    this.matSampler = device.createSampler({
      label: 'sampler:playmat',
      magFilter: 'linear', minFilter: 'linear', mipmapFilter: 'linear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
      maxAnisotropy: 16,
    });

    // A WGSL error does not throw -- it produces a pipeline that silently draws
    // nothing. Surfacing compilation messages turns "the screen is black" into
    // an actual file and line number.
    const module = (label: string, code: string) => {
      const source = `${commonWgsl}\n${code}`;
      const mod = device.createShaderModule({ label, code: source });
      void mod.getCompilationInfo().then((info) => {
        for (const m of info.messages) {
          if (m.type === 'info') continue;
          const where = `${label}.wgsl:${m.lineNum}:${m.linePos}`;
          const line = source.split('\n')[m.lineNum - 1]?.trim() ?? '';
          const log = m.type === 'error' ? console.error : console.warn;
          log(`[wgsl] ${where} ${m.message}\n    ${line}`);
        }
      });
      return mod;
    };

    const bgModule = module('background', backgroundWgsl);
    const cardModule = module('card', cardWgsl);
    const bloomModule = module('bloom', bloomWgsl);
    const tonemapModule = module('tonemap', tonemapWgsl);

    // ---- background ---------------------------------------------------------
    this.bgPipeline = device.createRenderPipeline({
      label: 'pipeline:background',
      layout: 'auto',
      vertex: { module: bgModule, entryPoint: 'vs' },
      fragment: { module: bgModule, entryPoint: 'fs', targets: [{ format: HDR_FORMAT }] },
      primitive: { topology: 'triangle-list' },
      depthStencil: {
        format: DEPTH_FORMAT,
        depthWriteEnabled: false,
        depthCompare: 'always',
      },
      multisample: { count: SAMPLE_COUNT },
    });
    this.bgBind = device.createBindGroup({
      label: 'bind:background',
      layout: this.bgPipeline.getBindGroupLayout(0),
      entries: [{ binding: 0, resource: { buffer: this.bgBuffer } }],
    });

    // ---- cards --------------------------------------------------------------
    const frameLayout = device.createBindGroupLayout({
      label: 'layout:frame',
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
      ],
    });
    const cardLayout = device.createBindGroupLayout({
      label: 'layout:card',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform', hasDynamicOffset: true, minBindingSize: CARD_UNIFORM_FLOATS * 4 },
        },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
      ],
    });

    this.cardPipeline = device.createRenderPipeline({
      label: 'pipeline:card',
      layout: device.createPipelineLayout({ bindGroupLayouts: [frameLayout, cardLayout] }),
      vertex: { module: cardModule, entryPoint: 'vs', buffers: [CARD_VERTEX_LAYOUT] },
      fragment: { module: cardModule, entryPoint: 'fs', targets: [{ format: HDR_FORMAT }] },
      primitive: { topology: 'triangle-list', cullMode: 'back', frontFace: 'ccw' },
      depthStencil: {
        format: DEPTH_FORMAT,
        depthWriteEnabled: true,
        // Reverse-Z: nearer geometry has the larger depth value.
        depthCompare: 'greater',
      },
      // Alpha-to-coverage turns the rounded-corner SDF into real MSAA coverage,
      // so the silhouette antialiases without alpha blending and without the
      // draw-order constraints that would come with it.
      multisample: { count: SAMPLE_COUNT, alphaToCoverageEnabled: true },
    });

    this.frameBind = device.createBindGroup({
      label: 'bind:frame',
      layout: frameLayout,
      entries: [
        { binding: 0, resource: { buffer: this.frameBuffer } },
        { binding: 1, resource: this.linearSampler },
      ],
    });

    // ---- table --------------------------------------------------------------
    // Shares the frame layout with the cards so it sees the same camera, and
    // draws before them with depth write on: a card lying on the mat should
    // occlude the markings under it.
    const tableModule = module('table', tableWgsl);
    this.tableBuffer = device.createBuffer({
      label: 'buffer:table',
      size: TABLE_UNIFORM_FLOATS * 4,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    const tableParamLayout = device.createBindGroupLayout({
      label: 'layout:table',
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
      ],
    });
    this.tableParamLayout = tableParamLayout;
    this.tablePipeline = device.createRenderPipeline({
      label: 'pipeline:table',
      layout: device.createPipelineLayout({
        bindGroupLayouts: [frameLayout, tableParamLayout],
      }),
      vertex: { module: tableModule, entryPoint: 'vs' },
      fragment: {
        module: tableModule,
        entryPoint: 'fs',
        targets: [{
          format: HDR_FORMAT,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha' },
          },
        }],
      },
      primitive: { topology: 'triangle-list' },
      depthStencil: {
        format: DEPTH_FORMAT,
        depthWriteEnabled: true,
        depthCompare: 'greater',
      },
      multisample: { count: SAMPLE_COUNT },
    });
    // Until a mat is supplied the print is a 1x1 black pixel, so the pass is
    // valid from the first frame and simply draws unprinted felt.
    this.matTexture = solidTexture(device, [0, 0, 0, 255]);
    this.matOwned = false;
    this.tableBind = this.makeTableBind();
    // ---- bloom --------------------------------------------------------------
    const bloomTargets: GPUColorTargetState[] = [{ format: HDR_FORMAT }];
    this.prefilterPipeline = device.createRenderPipeline({
      label: 'pipeline:bloom-prefilter',
      layout: 'auto',
      vertex: { module: bloomModule, entryPoint: 'vs' },
      fragment: { module: bloomModule, entryPoint: 'prefilter', targets: bloomTargets },
      primitive: { topology: 'triangle-list' },
    });
    this.downPipeline = device.createRenderPipeline({
      label: 'pipeline:bloom-down',
      layout: 'auto',
      vertex: { module: bloomModule, entryPoint: 'vs' },
      fragment: { module: bloomModule, entryPoint: 'downsample', targets: bloomTargets },
      primitive: { topology: 'triangle-list' },
    });
    this.upPipeline = device.createRenderPipeline({
      label: 'pipeline:bloom-up',
      layout: 'auto',
      vertex: { module: bloomModule, entryPoint: 'vs' },
      fragment: {
        module: bloomModule, entryPoint: 'upsample',
        targets: [{
          format: HDR_FORMAT,
          // Accumulate each level onto the one below it.
          blend: {
            color: { operation: 'add', srcFactor: 'one', dstFactor: 'one' },
            alpha: { operation: 'add', srcFactor: 'one', dstFactor: 'one' },
          },
        }],
      },
      primitive: { topology: 'triangle-list' },
    });

    // ---- tonemap ------------------------------------------------------------
    this.tonemapPipeline = device.createRenderPipeline({
      label: 'pipeline:tonemap',
      layout: 'auto',
      vertex: { module: tonemapModule, entryPoint: 'vs' },
      fragment: { module: tonemapModule, entryPoint: 'fs', targets: [{ format: gpu.format }] },
      primitive: { topology: 'triangle-list' },
    });

    this.disposeResize = gpu.onResize((w, h) => this.resize(w, h));
    if (gpu.width > 1 && gpu.height > 1) this.resize(gpu.width, gpu.height);
  }

  // -------------------------------------------------------------------------

  private resize(width: number, height: number): void {
    const device = this.device;
    this.releaseSized();

    this.msaa = device.createTexture({
      label: 'target:scene-msaa',
      size: [width, height],
      format: HDR_FORMAT,
      sampleCount: SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    this.msaaView = this.msaa.createView();

    this.resolve = device.createTexture({
      label: 'target:scene-resolve',
      size: [width, height],
      format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.resolveView = this.resolve.createView();

    this.depth = device.createTexture({
      label: 'target:depth',
      size: [width, height],
      format: DEPTH_FORMAT,
      sampleCount: SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    this.depthView = this.depth.createView();

    // Bloom chain, starting at half resolution.
    this.bloom = [];
    let w = Math.max(1, width >> 1);
    let h = Math.max(1, height >> 1);
    for (let i = 0; i < BLOOM_LEVELS && w > 2 && h > 2; i++) {
      const texture = device.createTexture({
        label: `bloom:${i}`,
        size: [w, h],
        format: HDR_FORMAT,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      });
      const params = device.createBuffer({
        label: `bloom:params:${i}`,
        size: 48,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });
      this.bloom.push({ texture, view: texture.createView(), width: w, height: h, params });
      w = Math.max(1, w >> 1);
      h = Math.max(1, h >> 1);
    }

    const level0 = this.bloom[0];
    if (level0) {
      this.prefilterBind = device.createBindGroup({
        label: 'bind:bloom-prefilter',
        layout: this.prefilterPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: this.resolveView },
          { binding: 1, resource: this.clampSampler },
          { binding: 2, resource: { buffer: level0.params } },
        ],
      });

      for (let i = 1; i < this.bloom.length; i++) {
        const prev = this.bloom[i - 1]!;
        const cur = this.bloom[i]!;
        cur.downBind = device.createBindGroup({
          label: `bind:bloom-down:${i}`,
          layout: this.downPipeline.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: prev.view },
            { binding: 1, resource: this.clampSampler },
            { binding: 2, resource: { buffer: cur.params } },
          ],
        });
      }
      // Upsample reads level i and writes into level i-1.
      for (let i = this.bloom.length - 1; i >= 1; i--) {
        const cur = this.bloom[i]!;
        cur.upBind = device.createBindGroup({
          label: `bind:bloom-up:${i}`,
          layout: this.upPipeline.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: cur.view },
            { binding: 1, resource: this.clampSampler },
            { binding: 2, resource: { buffer: cur.params } },
          ],
        });
      }

      this.tonemapBind = device.createBindGroup({
        label: 'bind:tonemap',
        layout: this.tonemapPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: this.resolveView },
          { binding: 1, resource: level0.view },
          { binding: 2, resource: this.clampSampler },
          { binding: 3, resource: { buffer: this.tonemapBuffer } },
        ],
      });
    }

    // Cached card bind groups reference nothing size-dependent, so they survive.
    this.sized = this.bloom.length > 0;
  }

  private releaseSized(): void {
    this.msaa?.destroy();
    this.resolve?.destroy();
    this.depth?.destroy();
    for (const b of this.bloom) {
      b.texture.destroy();
      b.params.destroy();
    }
    this.bloom = [];
    this.sized = false;
  }

  private ensureCardCapacity(count: number): void {
    if (count <= this.cardCapacity) return;
    const capacity = Math.max(count, this.cardCapacity * 2, 16);
    this.cardBuffer.destroy();
    this.cardBuffer = this.device.createBuffer({
      label: 'card-uniform',
      size: this.cardStride * capacity,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.cardData = new Float32Array((this.cardStride * capacity) / 4);
    this.cardCapacity = capacity;
    // Bind groups hold a reference to the old buffer.
    this.cardBinds.clear();
  }

  private cardBindGroup(visual: CardVisual): GPUBindGroup {
    let bind = this.cardBinds.get(visual.key);
    if (!bind) {
      bind = this.device.createBindGroup({
        label: `bind:card:${visual.key}`,
        layout: this.cardPipeline.getBindGroupLayout(1),
        entries: [
          {
            binding: 0,
            resource: { buffer: this.cardBuffer, offset: 0, size: CARD_UNIFORM_FLOATS * 4 },
          },
          { binding: 1, resource: visual.albedo.createView() },
          { binding: 2, resource: visual.mask.createView() },
          { binding: 3, resource: visual.back.createView() },
        ],
      });
      this.cardBinds.set(visual.key, bind);
    }
    return bind;
  }

  /** Drops cached bind groups for visuals that have been unloaded. */
  invalidateVisual(key: string): void {
    this.cardBinds.delete(key);
  }

  // -------------------------------------------------------------------------

  render(scene: Scene): void {
    if (!this.sized) return;

    const { device } = this;
    const { camera, environment: env, grade, cards, time } = scene;

    // ---- frame uniform ------------------------------------------------------
    const f = this.frameData;
    f.set(camera.viewProj, 0);
    f[16] = camera.position[0]; f[17] = camera.position[1]; f[18] = camera.position[2];
    f[19] = time;
    f[20] = env.lightDir[0]; f[21] = env.lightDir[1]; f[22] = env.lightDir[2];
    f[23] = env.lightIntensity;
    f[24] = env.lightColor[0]; f[25] = env.lightColor[1]; f[26] = env.lightColor[2];
    f[27] = env.exposure;
    f[28] = env.skyColor[0]; f[29] = env.skyColor[1]; f[30] = env.skyColor[2];
    f[31] = env.ambientMix;
    f[32] = env.groundColor[0]; f[33] = env.groundColor[1]; f[34] = env.groundColor[2];
    f[35] = 0;
    device.queue.writeBuffer(this.frameBuffer, 0, f);

    // ---- background uniform -------------------------------------------------
    const b = this.bgData;
    b[0] = this.gpu.width; b[1] = this.gpu.height; b[2] = time; b[3] = env.bgIntensity;
    b[4] = env.bgTop[0]; b[5] = env.bgTop[1]; b[6] = env.bgTop[2]; b[7] = 0;
    b[8] = env.bgBottom[0]; b[9] = env.bgBottom[1]; b[10] = env.bgBottom[2]; b[11] = 0;
    b[12] = env.bgAccent[0]; b[13] = env.bgAccent[1]; b[14] = env.bgAccent[2];
    b[15] = env.bgAccentStrength;
    b[16] = env.bgLightPos[0]; b[17] = env.bgLightPos[1]; b[18] = env.bgDust; b[19] = 0;
    device.queue.writeBuffer(this.bgBuffer, 0, b, 0, 20);

    // ---- table uniform ------------------------------------------------------
    const table = scene.table;
    if (table) {
      const t = this.tableData;
      t[0] = table.halfWidth; t[1] = table.halfDepth;
      t[2] = table.playerBaseZ; t[3] = table.opponentBaseZ;
      t[4] = table.battlefieldZ; t[5] = table.battlefieldSpacing;
      t[6] = table.battlefieldCount; t[7] = time;
      t[8] = table.playerHeld; t[9] = table.opponentHeld; t[10] = 0; t[11] = 0;
      t[12] = table.accent[0]; t[13] = table.accent[1]; t[14] = table.accent[2];
      t[15] = 0;
      device.queue.writeBuffer(this.tableBuffer, 0, t, 0, TABLE_UNIFORM_FLOATS);
    }

    // ---- per-card uniforms --------------------------------------------------
    this.ensureCardCapacity(cards.length);
    const stridef = this.cardStride / 4;
    const nrm = new Float32Array(12);
    for (let i = 0; i < cards.length; i++) {
      const c = cards[i]!;
      const o = i * stridef;
      this.cardData.set(c.model, o);
      normalMatrix(c.model, nrm);
      this.cardData.set(nrm, o + 16);
      this.cardData[o + 28] = c.foilTier;
      this.cardData[o + 29] = c.hover;
      this.cardData[o + 30] = c.bend;
      this.cardData[o + 31] = c.highlight;
      this.cardData[o + 32] = c.bendAxis[0];
      this.cardData[o + 33] = c.bendAxis[1];
      this.cardData[o + 34] = c.reveal;
      this.cardData[o + 35] = c.seed;
      this.cardData[o + 36] = c.settled;
      this.cardData[o + 37] = c.inHand;
      this.cardData[o + 38] = c.handDirect;
      this.cardData[o + 39] = c.handAmbient;
      this.cardData[o + 40] = c.tint[0];
      this.cardData[o + 41] = c.tint[1];
      this.cardData[o + 42] = c.tint[2];
      this.cardData[o + 43] = c.tint[3];
    }
    if (cards.length > 0) {
      device.queue.writeBuffer(
        this.cardBuffer, 0, this.cardData, 0, cards.length * stridef,
      );
    }

    // ---- bloom params -------------------------------------------------------
    const knee = Math.max(grade.bloomKnee, 1e-4);
    for (let i = 0; i < this.bloom.length; i++) {
      const lvl = this.bloom[i]!;
      // texelSize is that of the level being *sampled*, not written.
      const src = i === 0 ? { width: this.gpu.width, height: this.gpu.height } : this.bloom[i - 1]!;
      device.queue.writeBuffer(lvl.params, 0, new Float32Array([
        1 / src.width, 1 / src.height, 0, 0,
        grade.bloomThreshold, grade.bloomThreshold - knee, 2 * knee, 0.25 / knee,
        1.0, 0, 0, 0,
      ]));
    }

    // ---- tonemap params -----------------------------------------------------
    const t = this.tonemapData;
    t[0] = 1 / this.gpu.width; t[1] = 1 / this.gpu.height;
    t[2] = 1.0; t[3] = grade.bloomIntensity;
    t[4] = grade.vignette; t[5] = grade.grain; t[6] = grade.chromatic; t[7] = time;
    t[8] = grade.saturation; t[9] = grade.contrast; t[10] = 0; t[11] = 0;
    device.queue.writeBuffer(this.tonemapBuffer, 0, t);

    // ---- record -------------------------------------------------------------
    const encoder = device.createCommandEncoder({ label: 'frame' });

    {
      const pass = encoder.beginRenderPass({
        label: 'pass:scene',
        colorAttachments: [{
          view: this.msaaView,
          resolveTarget: this.resolveView,
          loadOp: 'clear',
          storeOp: 'discard', // only the resolve is read downstream
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
        depthStencilAttachment: {
          view: this.depthView,
          depthLoadOp: 'clear',
          depthStoreOp: 'discard',
          depthClearValue: 0, // reverse-Z far plane
        },
      });

      pass.setPipeline(this.bgPipeline);
      pass.setBindGroup(0, this.bgBind);
      pass.draw(3);

      // The mat, before the cards so they sit on top of their slots.
      if (scene.table) {
        pass.setPipeline(this.tablePipeline);
        pass.setBindGroup(0, this.frameBind);
        pass.setBindGroup(1, this.tableBind);
        pass.draw(6);
      }

      if (cards.length > 0) {
        pass.setPipeline(this.cardPipeline);
        pass.setBindGroup(0, this.frameBind);
        pass.setVertexBuffer(0, this.mesh.vertices);
        pass.setIndexBuffer(this.mesh.indices, this.mesh.indexFormat);
        for (let i = 0; i < cards.length; i++) {
          pass.setBindGroup(1, this.cardBindGroup(cards[i]!.visual), [i * this.cardStride]);
          pass.drawIndexed(this.mesh.indexCount);
        }
      }
      pass.end();
    }

    // Prefilter into level 0.
    this.blit(encoder, 'pass:bloom-prefilter', this.prefilterPipeline, this.prefilterBind, this.bloom[0]!.view, false);

    // Downsample the chain.
    for (let i = 1; i < this.bloom.length; i++) {
      const lvl = this.bloom[i]!;
      this.blit(encoder, `pass:bloom-down:${i}`, this.downPipeline, lvl.downBind!, lvl.view, false);
    }

    // Upsample back down the chain, adding each level into the one below.
    for (let i = this.bloom.length - 1; i >= 1; i--) {
      const lvl = this.bloom[i]!;
      const dst = this.bloom[i - 1]!;
      this.blit(encoder, `pass:bloom-up:${i}`, this.upPipeline, lvl.upBind!, dst.view, true);
    }

    // Composite to the swapchain.
    this.blit(
      encoder, 'pass:tonemap', this.tonemapPipeline, this.tonemapBind,
      this.gpu.context.getCurrentTexture().createView(), false,
    );

    device.queue.submit([encoder.finish()]);
  }

  private blit(
    encoder: GPUCommandEncoder,
    label: string,
    pipeline: GPURenderPipeline,
    bind: GPUBindGroup,
    view: GPUTextureView,
    load: boolean,
  ): void {
    const pass = encoder.beginRenderPass({
      label,
      colorAttachments: [{
        view,
        loadOp: load ? 'load' : 'clear',
        storeOp: 'store',
        clearValue: { r: 0, g: 0, b: 0, a: 1 },
      }],
    });
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bind);
    pass.draw(3);
    pass.end();
  }

  /**
   * Supplies the printed playmat. The renderer takes ownership of the texture.
   *
   * Separate from `render` because the mat is painted once from the zone table
   * rather than per frame, and rebuilding the bind group every frame would
   * churn one for no reason.
   */
  setPlaymat(texture: GPUTexture): void {
    if (this.matOwned) this.matTexture.destroy();
    this.matTexture = texture;
    this.matOwned = true;
    this.tableBind = this.makeTableBind();
  }

  private makeTableBind(): GPUBindGroup {
    return this.device.createBindGroup({
      label: 'bind:table',
      layout: this.tableParamLayout,
      entries: [
        { binding: 0, resource: { buffer: this.tableBuffer } },
        { binding: 1, resource: this.matTexture.createView() },
        { binding: 2, resource: this.matSampler },
      ],
    });
  }

  destroy(): void {
    this.matTexture.destroy();
    this.disposeResize();
    this.releaseSized();
    destroyCardMesh(this.mesh);
    this.frameBuffer.destroy();
    this.bgBuffer.destroy();
    this.tonemapBuffer.destroy();
    this.cardBuffer.destroy();
    this.cardBinds.clear();
  }
}
